import { Dispatch, FC, Fragment, useEffect, useState } from "react";
import {
    Box, FormControl, Grid, InputLabel, Select, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, styled, tableCellClasses
} from "@mui/material";
import { useStyles } from '../styles';
import { LoadingButton } from "@mui/lab";
import SaveIcon from '@mui/icons-material/Save';
import { DataRef } from "../../../types/DataRef";
import { AlertType } from "../../../types/AlertType";
import { AxiosInstance } from "axios";
import CheckAttr from "../../../utils/CheckAttr";
import { DataCout } from "../../content/parametrage/Cout/Cout";
import AddCoutRowForm from "./AddCoutRowForm";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
        textAlign: 'left'
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        textAlign: 'center'
    },
}));

export interface ArticleRow {
    id: number,
    libelle: string,
    id_association: number;
    code_article: DataRef;
    devise: DataRef;
    cout: {
        id?: number,
        date_debut?: Date,
        date_fin?: Date,
        coutactivite?: string
    }
}

export interface PivotDatas {
    id: number,
    societe_fact: DataRef;
    code_gesco: DataRef;
    articles: ArticleRow[]
}

export interface EditCout {
    idPivot: number;
    idArticle: number;
    coutactivite: string;
}

export interface Cout {
    id_cout?: number;
    article: number;
    coutactivite?: string;
}

interface CoutToSave {
    pays?: number;
    client?: number;
    operation?: number;
    date_debut?: Date;
    date_fin?: Date;
    couts: Cout[];
}

interface InputValidation {
    pays: boolean;
    client: boolean;
    operation: boolean;
    date_debut: boolean;
    date_fin: boolean;
}

interface SaisieAgentFormProps {
    onClose: () => void;
    cout?: DataCout;
    dataPaysInAssociations: DataRef[];
    dataAllClients: DataRef[];
    http: AxiosInstance;
    setOpenSnack: Dispatch<React.SetStateAction<boolean>>;
    setAlerteMessage: Dispatch<React.SetStateAction<string>>;
    setAlertType: Dispatch<React.SetStateAction<AlertType>>;
    reloadList: () => void;
}

const resetCoutToSave: CoutToSave = {
    pays: undefined,
    client: undefined,
    operation: undefined,
    date_debut: undefined,
    date_fin: undefined,
    couts: []
}

const resetInpuValidation: InputValidation = {
    pays: false,
    client: false,
    operation: false,
    date_debut: false,
    date_fin: false
};

export interface DataOperation {
    id: number;
    libelle: string;
    EstFormation: boolean;
}

const AddCoutForm: FC<SaisieAgentFormProps> = ({
    dataPaysInAssociations,
    dataAllClients,
    onClose,
    setOpenSnack,
    setAlerteMessage,
    setAlertType,
    http,
    reloadList
}) => {
    const classes = useStyles();

    const [isClickSave, setIsClickSave] = useState<boolean>(false);

    const [dataOperations, setDataOperations] = useState<DataRef[]>([]);
    const [dataArticles, setDataArticles] = useState<PivotDatas[]>([]);
    const [coutToSave, setCoutToSave] = useState<CoutToSave>(resetCoutToSave);
    const [errorInput, setErrorInput] = useState<InputValidation>(resetInpuValidation);

    useEffect(() => {
        if (coutToSave?.client) {
            // Récupère les operations du clients
            http.get(`operation/get-operations/via-client/${coutToSave?.client}`)
                .then(res => {
                    Array.isArray(res.data) && setDataOperations(res.data);
                }).catch(error => {
                    console.log(error);
                });
        } else {
            setCoutToSave({
                ...coutToSave,
                operation: undefined,
                couts: []
            });
            setDataOperations([]);
        }
    }, [coutToSave?.client]);

    useEffect(() => {
        if (
            coutToSave?.date_debut &&
            coutToSave?.date_fin &&
            (coutToSave?.date_debut <= coutToSave?.date_fin)
        ) {
            // Récupère les opérations
            http.post('pivot/socifact-codegesco/get-cout-data', {
                pays: coutToSave?.pays,
                client: coutToSave?.client,
                operation: coutToSave?.operation,
                date_debut: coutToSave?.date_debut,
                date_fin: coutToSave?.date_fin
            }).then(res => {
                Array.isArray(res?.data) && setDataArticles(res?.data);
            }).catch(error => {
                console.log(error);
            });
        } else {
            setCoutToSave({
                ...coutToSave,
                couts: []
            });
        }
    }, [
        coutToSave?.pays,
        coutToSave?.client,
        coutToSave?.operation,
        coutToSave?.date_debut,
        coutToSave?.date_fin
    ]);

    const handleChange = (e: any) => {
        const { name, value } = e.target;

        setCoutToSave({
            ...coutToSave,
            [name]: value
        });
    }

    const handleChangeCout = (editCout: EditCout): void => {
        let tmpArr = dataArticles;

        const assocIndex = tmpArr.findIndex(elem => elem.id === Number(editCout?.idPivot));
        if (assocIndex > -1) {
            const articleIndex = tmpArr[assocIndex]?.articles.findIndex(elem => elem.id === Number(editCout?.idArticle));

            if (articleIndex > -1) {
                tmpArr[assocIndex].articles[articleIndex]!.cout.coutactivite = editCout?.coutactivite as string;
            }
        }

        setDataArticles([...tmpArr]);
    };

    const isValidateCouts = (datas: CoutToSave): boolean => {

        // Contient les lignes qui n'ont pas encore parametré
        const tmpCouts: number[] = [];

        // Continet le nombre de ligne qui sont déja parametré
        let dejaParametrer = 0;
        for (const cout of datas?.couts) {
            if (CheckAttr(cout?.id_cout)) {
                dejaParametrer++;
            } else {
                if (CheckAttr(cout.coutactivite)) {
                    tmpCouts.push(cout.article);
                }
            }
        }

        if (tmpCouts.length === 0) {
            if (datas?.couts?.length === dejaParametrer) {
                setAlerteMessage("Merci de saisir des coûts pour une nouvelle période SVP !");
                setAlertType("warning");
                setOpenSnack(true);
            } else {
                setAlerteMessage("Merci de paramétrer au moins un cout parmi ceux qui n’ont pas encore de coût SVP !");
                setAlertType("warning");
                setOpenSnack(true);
            }

            return false;
        }

        return true;
    }

    const isValidate = (datas: CoutToSave): boolean => {
        // Reinitialise les variables des validations
        setErrorInput(resetInpuValidation);

        const auMoinsUneLigneParametrer = isValidateCouts(datas);

        if (auMoinsUneLigneParametrer) {
            if (!CheckAttr(datas?.pays) || !CheckAttr(datas?.client) || !CheckAttr(datas?.operation) ||
                !CheckAttr(datas?.couts) || !CheckAttr(datas?.date_debut) || !CheckAttr(datas?.date_fin)) {
                if (!CheckAttr(coutToSave?.pays)) {
                    setErrorInput((prev: InputValidation) => ({ ...prev, pays: true }));
                }

                if (!CheckAttr(coutToSave?.client)) {
                    setErrorInput((prev: InputValidation) => ({ ...prev, client: true }));
                }

                if (!CheckAttr(coutToSave?.operation)) {
                    setErrorInput((prev: InputValidation) => ({ ...prev, bu: true }));
                }

                if (!CheckAttr(coutToSave?.date_debut)) {
                    setErrorInput((prev: InputValidation) => ({ ...prev, date_debut: true }));
                }

                if (!CheckAttr(coutToSave?.date_fin)) {
                    setErrorInput((prev: InputValidation) => ({ ...prev, date_fin: true }));
                }

                if (!CheckAttr(coutToSave?.couts)) {
                    setErrorInput((prev: InputValidation) => ({ ...prev, operation: true }));
                }

                setAlerteMessage("Merci de remplir les champs en rouge SVP!");
                setAlertType("warning");
                setOpenSnack(true);

                return false;
            }
        } else {
            return false;
        }

        return true;
    }

    const getOperationsId = (CoutsToCheck: CoutToSave): number[] => {
        let idOperations: number[] = [];

        for (const cout of CoutsToCheck.couts) {
            if (!CheckAttr(cout?.id_cout)) {
                idOperations.push(cout.article);
            }
        }

        return idOperations;
    }

    const reorganiseCoutToSave = (): Cout[] => {
        const tmpCouts: Cout[] = [];
        for (const pivot of dataArticles) {
            for (const article of pivot.articles) {
                tmpCouts.push({
                    id_cout: article?.cout?.id ?? undefined,
                    article: article?.id,
                    coutactivite: article?.cout?.coutactivite
                });
            }
        }

        return tmpCouts;
    }

    const handleSave = () => {
        // On deplace les couts dans  dataArticles vers couts to save
        const reorganisedDatas = reorganiseCoutToSave();
        const finalDatas = { ...coutToSave, couts: reorganisedDatas };

        if (isValidate(finalDatas)) {
            setIsClickSave(true);

            const dataToCheck = {
                dateDebut: coutToSave.date_debut,
                dateFin: coutToSave.date_fin,
                arrayIdOperation: getOperationsId(finalDatas),
                type: 'Cout'
            };

            // Control chevauchement
            http.post(`check/chevauchement`, dataToCheck)
                .then(res => {
                    const overlapCout = res.data[0];

                    if (overlapCout?.article) {
                        setAlerteMessage(`Il y a déjà un cout paramétré pour l’article ${overlapCout?.article?.libelle} à l’intervalle de date saisi !`);
                        setAlertType("warning");
                        setOpenSnack(true);
                        setIsClickSave(false);
                    } else {
                        const coutsToFilter = finalDatas?.couts;

                        const filtedCouts = coutsToFilter?.filter(elem => !elem?.id_cout && CheckAttr(elem?.coutactivite));

                        http.post('cout/save', { ...finalDatas, couts: filtedCouts })
                            .then(() => {
                                setAlerteMessage("Enregistrement efféctuée !");
                                setAlertType("success");
                                setOpenSnack(true);
                                onClose();
                                reloadList();
                            }).catch(error => {
                                console.log(error);
                                setAlertType("error");
                                setOpenSnack(true);
                            }).finally(() => {
                                setIsClickSave(false);
                            });
                    }
                }).catch(error => {
                    console.log(error);
                    setAlertType("error");
                    setOpenSnack(true);
                    setIsClickSave(false);
                });
        }
    }

    const articlesCount = dataArticles?.length ? true : false;

    return (
        <Fragment>
            <Box className={classes.root}>
                <Grid container spacing={2} columns={{ xs: 6, sm: 12, md: 12 }}>
                    <Grid item xs={6} sm={6} md={6}>
                        <FormControl variant="outlined" className={classes.formControl} sx={{ marginLeft: 1, marginTop: 1 }}>
                            <InputLabel
                                htmlFor="pays"
                                color="warning"
                                shrink={CheckAttr(coutToSave?.pays)}
                            >
                                Pays de production
                            </InputLabel>
                            <Select
                                id="pays"
                                native
                                color="warning"
                                error={errorInput.pays}
                                value={coutToSave?.pays}
                                onChange={handleChange}
                                label="Pays de production"
                                inputProps={{
                                    name: 'pays',
                                }}
                            >
                                <option value=""></option>
                                {dataPaysInAssociations.map((pays) => (
                                    <option key={pays?.id} value={pays?.id}>
                                        {pays?.libelle}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={6} md={6}>
                        <FormControl variant="outlined" className={classes.formControl} sx={{ marginLeft: 1, marginTop: 1 }}>
                            <InputLabel
                                htmlFor="client"
                                color="warning"
                                shrink={CheckAttr(coutToSave?.client)}
                            >
                                Client
                            </InputLabel>
                            <Select
                                id="client"
                                native
                                color="warning"
                                error={errorInput.client}
                                value={coutToSave?.client}
                                onChange={handleChange}
                                label="Client"
                                inputProps={{
                                    name: 'client',
                                }}
                            >
                                <option value=""></option>
                                {dataAllClients.map((client) => (
                                    <option key={client?.id} value={client?.id}>
                                        {client?.libelle}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                <Grid container spacing={2} columns={{ xs: 6, sm: 12, md: 12 }}>
                    <Grid item xs={6} sm={6} md={6}>
                        <FormControl variant="outlined" className={classes.formControl} sx={{ marginLeft: 1, marginTop: 1 }}>
                            <InputLabel
                                htmlFor="operation"
                                color="warning"
                                shrink={CheckAttr(coutToSave?.operation)}
                            >
                                Opération
                            </InputLabel>
                            <Select
                                id="operation"
                                native
                                color="warning"
                                error={errorInput.operation}
                                value={coutToSave?.operation}
                                onChange={handleChange}
                                label="Opération"
                                inputProps={{
                                    name: 'operation',
                                }}
                            >
                                <option value=""></option>
                                {dataOperations.map((operation) => (
                                    <option key={operation?.id} value={operation?.id}>
                                        {operation?.libelle}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                <Grid container spacing={2} columns={{ xs: 6, sm: 12, md: 12 }} sx={{ mt: 1 }}>
                    <Grid item xs={6} sm={6} md={6}>
                        <TextField
                            name="date_debut"
                            label="Date debut"
                            type="date"
                            color="warning"
                            fullWidth
                            error={errorInput.date_debut}
                            value={coutToSave?.date_debut}
                            onChange={handleChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={6} md={6}>
                        <TextField
                            name="date_fin"
                            label="Date fin"
                            type="date"
                            color="warning"
                            fullWidth
                            error={errorInput.date_fin}
                            value={coutToSave?.date_fin}
                            onChange={handleChange}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                </Grid>
            </Box >
            {
                articlesCount && (
                    <>
                        <TableContainer sx={{ mt: 4 }}>
                            <Table sx={{ minWidth: 700 }} aria-label="customized table">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>Société de facturation</StyledTableCell>
                                        <StyledTableCell>Code gesco</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {
                                        dataArticles.map(association => (
                                            <Fragment>
                                                <AddCoutRowForm
                                                    key={association.id}
                                                    association={association}
                                                    handleChangeCout={handleChangeCout}
                                                />
                                            </Fragment>
                                        ))
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box display="flex" justifyContent="center" mt={5}>
                            <LoadingButton
                                onClick={() => handleSave()}
                                endIcon={<SaveIcon />}
                                loading={isClickSave}
                                loadingPosition="end"
                                sx={{ textTransform: "none" }}
                                variant="contained"
                                color="warning"
                            >
                                Enregistrer
                            </LoadingButton>
                        </Box>
                    </>
                )
            }
        </Fragment >
    );
};

export default AddCoutForm;